import { type CanvasHitInput, canEdit, type PointerKind } from '@better-lights/core';
import { projectState } from '../../state/documentStore.ts';
import { toast } from '../../state/feedback.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { setUi, useUi } from '../../state/uiStore.ts';
import type { Live } from './live.ts';
import { flatPoints } from './overlay.ts';

export const PENCIL_TOAST = 'Stylus detected: the stylus draws, fingers pan and zoom. Change in Lights › Input.';

export function pointerKind(e: PointerEvent): PointerKind {
  if (e.pointerType === 'pen') return 'stylus';
  if (e.pointerType === 'touch') return 'touch';
  if (e.pointerType === 'mouse') return 'mouse';
  return 'other';
}

/** `canEdit` with the auto-mode "Pencil seen" flag remembered for the app run. */
export function mayEdit(kind: PointerKind): boolean {
  const mode = useSettings.getState().pencilMode;
  const seen = useUi.getState().pencilSeen;
  const r = canEdit(kind, mode, seen);
  if (r.pencilSeen && !seen) {
    setUi({ pencilSeen: true });
    if (mode === 'auto') toast(PENCIL_TOAST);
  }
  return r.canEdit;
}

/** Hit-test input from the document store + live transform. */
export function hitInput(
  live: Live,
  sx: number,
  sy: number,
  pointer: PointerKind,
  editing: boolean,
): CanvasHitInput | null {
  const s = projectState();
  if (!s.photo) return null;
  const W = s.photo.width;
  const H = s.photo.height;
  const runs = s.runs.map((r) => flatPoints(r, W, H));
  const focusId = s.activeRunId ?? s.selectedRunId;
  const focus = focusId ? s.runs.findIndex((r) => r.id === focusId) : -1;
  const drawing = !!s.activeRunId && s.runs.some((r) => r.id === s.activeRunId);
  return { runs, focus, drawing, view: live.transform, sx, sy, pointer, editing, W, H };
}
